import GuideLayout from '../../layouts/GuideLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { ResourceLink } from '../../components/content/ResourceLink';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';

const tocItems: TocItem[] = [
  { id: 'overview', label: 'Overview', level: 2 },
  { id: 'outbound-tiers', label: 'Outbound Internet Tiers', level: 2 },
  { id: 'inter-region', label: 'Inter-Region Transfer', level: 2 },
  { id: 'inbound', label: 'Inbound Data (Free)', level: 2 },
  { id: 'worked-examples', label: 'Worked Examples', level: 2 },
  { id: 'calculator', label: 'Bandwidth Calculator', level: 2 },
  { id: 'cost-reduction', label: 'Reducing Egress Costs', level: 2 },
  { id: 'resources', label: 'Official Resources', level: 2 },
];

const bottomCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-900 Microsoft Azure Fundamentals"
    url="https://www.pluralsight.com/paths/az-900-microsoft-azure-fundamentals"
    price="From £29/month"
    badgeText="Covers Networking & Pricing"
  />
);

export default function BandwidthCostsPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Azure Egress & Bandwidth Costs Explained UK 2026',
            description:
              'How Azure charges for outbound internet traffic and inter-region data transfer — tiered pricing in plain English for UK South.',
            author: { '@type': 'Organization', name: 'AzureCalc.uk' },
            publisher: {
              '@type': 'Organization',
              name: 'AzureCalc.uk',
              url: 'https://www.azure-calc.co.uk',
            },
            dateModified: '2026-03-30',
            url: 'https://www.azure-calc.co.uk/guides/azure-egress-bandwidth-costs-explained',
          }),
        }}
      />
      <GuideLayout
        title="Azure Egress & Bandwidth Costs Explained UK 2026"
        description="How Azure charges for outbound internet traffic and inter-region data transfer — tiered pricing in plain English for UK South in GBP. Updated March 2026."
        lastUpdated="March 2026"
        canonical="https://www.azure-calc.co.uk/guides/azure-egress-bandwidth-costs-explained"
        metaTitle="Azure Egress & Bandwidth Costs Explained UK 2026 | AzureCalc.uk"
        metaDescription="Understand Azure outbound internet and inter-region transfer charges for UK South in GBP, with worked examples and a bandwidth calculator."
        tocItems={tocItems}
        relatedCalculators={[
          { service: 'bandwidth', path: '/bandwidth', label: 'Bandwidth Calculator' },
        ]}
        bottomCta={bottomCta}
      >
        <p className="text-[15px] italic mb-8" style={{ color: 'var(--text-tertiary)' }}>
          Outbound internet egress and inter-region transfer costs explained in plain English.
          The first 100 GB/month is free — here is how the tiers work above that.
        </p>

        <p className="text-[12px] italic mb-6" style={{ color: 'var(--text-tertiary)' }}>
          Built and verified by an independent Azure engineer, frustrated with the official calculator.
        </p>

        {/* SECTION 1 */}
        <section id="overview">
          <h2>Overview</h2>
          <p>
            Start with what is free, because there is more of it than most engineers realise.
            Inbound data — everything coming into Azure from the internet — is free with no
            cap. Availability zone (AZ) to AZ data transfers within UK South have been free
            since 2023. The first 100 GB of outbound internet egress per month is free.
            If your application mostly receives data and processes it within a single region,
            your bandwidth bill may genuinely be zero.
          </p>
          <p>
            What costs money: outbound internet traffic above 100 GB/month, inter-region
            transfers (for example UK South to UK West or to West Europe), CDN origin pull
            traffic, and Private Endpoint data processing charges. These meters compound
            — the same data can generate a charge when it leaves a VM, a second charge when
            it crosses a regional boundary, and a third if it passes through a gateway
            with its own data-processing meter.
          </p>
          <p>
            Outbound internet egress is tiered: the more data you transfer, the lower the
            per-GB rate. Inter-region transfers within Europe are a flat rate from the
            first byte with no free tier. All prices below are for UK South in GBP,
            sourced from the Azure Retail Prices API and updated nightly.
          </p>
          <div className="not-prose rounded p-4 my-6" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0,120,212,0.4)' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#0078d4] text-[20px] shrink-0 mt-0.5">
                info
              </span>
              <div className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                <strong>Key rule:</strong> Inbound data is always free. Outbound internet egress
                is free for the first 100 GB/month. Inter-region transfers (e.g. UK South ↔ UK
                West) are charged from the first byte.
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 */}
        <section id="outbound-tiers">
          <h2>Outbound Internet Tiers</h2>
          <p>
            Outbound internet egress is charged in five tiers. Each tier applies only to the
            data within that band — not retroactively to all data once you cross a threshold.
          </p>
          <div className="not-prose overflow-x-auto my-6">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--outline-variant)' }}>
                  <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Tier</th>
                  <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Monthly volume</th>
                  <th className="text-right py-2 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Rate (GBP)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['1', 'First 100 GB', 'Free'],
                  ['2', '100 GB – 10,000 GB', '~£0.070/GB'],
                  ['3', '10,000 GB – 50,000 GB', '~£0.067/GB'],
                  ['4', '50,000 GB – 150,000 GB', '~£0.056/GB'],
                  ['5', '150,000 GB – 500,000 GB', '~£0.040/GB'],
                ].map(([tier, range, rate]) => (
                  <tr key={tier} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-2 pr-6" style={{ color: 'var(--text-tertiary)' }}>Tier {tier}</td>
                    <td className="py-2 pr-6" style={{ color: 'var(--text-secondary)' }}>{range}</td>
                    <td
                      className="py-2 text-right font-semibold"
                      style={{ color: rate === 'Free' ? '#4ec94e' : '#c7e0f4' }}
                    >
                      {rate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            GBP rates are approximate — use the calculator below for exact current figures. Rates
            are converted from USD at the time of the Azure Retail Prices API update. The USD
            source rates are $0.087, $0.083, $0.070, and $0.050/GB for tiers 2–5.
          </p>
        </section>

        {/* SECTION 3 */}
        <section id="inter-region">
          <h2>Inter-Region Transfer</h2>
          <p>
            Data transferred between Azure regions (for example, from UK South to UK West, or
            from UK South to West Europe) is charged separately from internet egress. There is
            no free tier for inter-region transfers — billing starts from the first byte.
          </p>
          <div className="not-prose overflow-x-auto my-6">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--outline-variant)' }}>
                  <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Transfer type</th>
                  <th className="text-right py-2 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Rate (GBP)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Within Europe (e.g. UK South ↔ UK West)', '~£0.016/GB'],
                  ['Europe to North America', '~£0.070/GB'],
                  ['Europe to Asia Pacific', '~£0.080/GB'],
                  ['Same region (within UK South)', 'Free'],
                ].map(([type, rate]) => (
                  <tr key={type} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-2 pr-6" style={{ color: 'var(--text-secondary)' }}>{type}</td>
                    <td
                      className="py-2 text-right font-semibold"
                      style={{ color: rate === 'Free' ? '#4ec94e' : '#c7e0f4' }}
                    >
                      {rate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Data transferred between services within the same Azure region is free — for example,
            a VM in UK South reading from a storage account in UK South incurs no bandwidth
            charge. Always co-locate services in the same region to avoid inter-region costs.
          </p>
        </section>

        {/* SECTION 4 */}
        <section id="inbound">
          <h2>Inbound Data — Always Free</h2>
          <p>
            All inbound data transfer to Azure is free regardless of volume, source, or service.
            This includes:
          </p>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              'Uploads from the internet to Azure Blob Storage',
              'Data sent from on-premises to Azure (over internet or ExpressRoute)',
              'API requests from clients to Azure-hosted services',
              'Data ingested into Log Analytics workspaces',
              'Traffic between the internet and Azure API Management',
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 px-4 py-2.5 rounded bg-[#107c10]/10 border border-[#107c10]/30"
              >
                <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">
                  check_circle
                </span>
                <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>{item}</span>
              </div>
            ))}
          </div>
          <p>
            ExpressRoute has its own pricing model — the circuit itself has a monthly fee and
            metered data plans charge per GB for outbound data. Inbound over ExpressRoute is
            free on unlimited plans and charged per-GB on metered plans.
          </p>
        </section>

        {/* SECTION 5 */}
        <section id="worked-examples">
          <h2>Worked Examples</h2>

          <h3>Example 1 — Small web application (50 GB/month outbound)</h3>
          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <table className="w-full text-[13px]">
              <tbody>
                {[
                  ['Monthly outbound', '50 GB'],
                  ['Tier 1 (first 100 GB)', 'Free'],
                  ['Total bandwidth cost', '£0.00'],
                ].map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-1.5 w-48" style={{ color: 'var(--text-tertiary)' }}>{k}</td>
                    <td className="py-1.5" style={{ color: 'var(--text-secondary)' }}>{v}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--outline-variant)' }}>
                  <td className="py-2 font-bold" style={{ color: 'var(--text-primary)' }}>Monthly total</td>
                  <td className="py-2 font-bold text-[#107c10]">Free</td>
                </tr>
              </tbody>
            </table>
            <p className="text-[12px] mt-3 italic" style={{ color: 'var(--text-tertiary)' }}>
              Small workloads often pay nothing for bandwidth — the 100 GB free tier covers most
              low-traffic applications.
            </p>
          </div>

          <h3>Example 2 — Media streaming platform (8,000 GB/month outbound)</h3>
          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <table className="w-full text-[13px]">
              <tbody>
                {[
                  ['Monthly outbound', '8,000 GB'],
                  ['Tier 1: first 100 GB', 'Free'],
                  ['Tier 2: next 7,900 GB × £0.070', '£553.00'],
                  ['Total bandwidth cost', '£553.00'],
                ].map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-1.5 w-48" style={{ color: 'var(--text-tertiary)' }}>{k}</td>
                    <td className="py-1.5" style={{ color: 'var(--text-secondary)' }}>{v}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--outline-variant)' }}>
                  <td className="py-2 font-bold" style={{ color: 'var(--text-primary)' }}>Monthly total</td>
                  <td className="py-2 font-bold text-[#0078d4]">~£553</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Example 3 — Multi-region replication (500 GB/month UK South → UK West)</h3>
          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <table className="w-full text-[13px]">
              <tbody>
                {[
                  ['Monthly inter-region transfer', '500 GB'],
                  ['Rate (within Europe)', '~£0.016/GB'],
                  ['Inter-region cost', '500 × £0.016 = £8.00'],
                  ['Free tier applies?', 'No — inter-region has no free tier'],
                ].map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-1.5 w-48" style={{ color: 'var(--text-tertiary)' }}>{k}</td>
                    <td className="py-1.5" style={{ color: 'var(--text-secondary)' }}>{v}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--outline-variant)' }}>
                  <td className="py-2 font-bold" style={{ color: 'var(--text-primary)' }}>Monthly total</td>
                  <td className="py-2 font-bold text-[#0078d4]">~£8</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* SECTION 6 */}
        <section id="calculator">
          <h2>Bandwidth Calculator</h2>
          <p>
            Use our UK South bandwidth calculator to estimate exact outbound internet and
            inter-region costs for your workload. Enter your monthly GB volumes to see the
            tiered breakdown.
          </p>
          <div className="not-prose mt-4">
            <CalculatorEmbed service="bandwidth" />
          </div>
        </section>

        {/* SECTION 7 */}
        <section id="cost-reduction">
          <h2>Reducing Egress Costs</h2>
          <p>
            Bandwidth costs can be one of the largest unexpected items on an Azure bill for
            data-heavy workloads. These strategies reduce or eliminate egress charges:
          </p>
          <div className="not-prose flex flex-col gap-3 my-6">
            {[
              {
                title: 'Use Azure CDN for public content',
                detail:
                  'CDN egress rates are lower than origin egress rates. Serving assets via Azure CDN (or Azure Front Door) reduces the volume of direct origin egress and improves performance.',
                icon: 'language',
              },
              {
                title: 'Co-locate services in the same region',
                detail:
                  'Data transfer between services in the same Azure region is free. Moving a database and its application server to the same region eliminates inter-region charges.',
                icon: 'location_on',
              },
              {
                title: 'Use Private Endpoints for internal traffic',
                detail:
                  'Traffic routed through Private Endpoints stays on the Microsoft backbone and avoids internet egress charges. Essential for high-volume internal service-to-service communication.',
                icon: 'lock',
              },
              {
                title: 'Use Azure ExpressRoute Unlimited for high volumes',
                detail:
                  'ExpressRoute Unlimited plans have a flat monthly fee with no per-GB outbound charge. Cost-effective for workloads moving hundreds of TB/month to on-premises.',
                icon: 'swap_horiz',
              },
              {
                title: 'Compress data before transfer',
                detail:
                  'Azure charges for bytes transferred, not logical data. Enabling compression (gzip/Brotli at the application layer) reduces billable egress volume directly.',
                icon: 'compress',
              },
            ].map((tip) => (
              <div
                key={tip.title}
                className="flex gap-4 border rounded p-4"
                style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}
              >
                <span className="material-symbols-outlined text-[#0078d4] text-[22px] shrink-0 mt-0.5">
                  {tip.icon}
                </span>
                <div>
                  <p className="text-[14px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{tip.title}</p>
                  <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>{tip.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 8 */}
        <section id="resources">
          <h2>Official Resources</h2>
          <div className="not-prose flex flex-col gap-2 my-4">
            <ResourceLink
              title="Azure Bandwidth pricing"
              url="https://azure.microsoft.com/en-gb/pricing/details/bandwidth/"
              source="Microsoft Azure"
            />
            <ResourceLink
              title="Data transfer pricing explained"
              url="https://learn.microsoft.com/en-us/azure/networking/azure-network-latency"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="Azure CDN pricing"
              url="https://azure.microsoft.com/en-gb/pricing/details/cdn/"
              source="Microsoft Azure"
            />
          </div>
        </section>
      </GuideLayout>
    </>
  );
}
